import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white py-20 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Navigation */}
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/'}
            className="border-golden/20 text-golden hover:bg-golden hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-golden/10 text-golden border-golden/20 mb-4">Legal</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-golden">
            Privacy Policy
          </h1>
          <p className="text-gray-400">Upper Circuit Stocks by Chirag Rathod</p>
          <p className="text-sm text-gray-500 mt-2">Last updated: October 2024</p>
        </div>

        <Card className="bg-gray-900/50 border-golden/20 mb-8">
          <CardHeader>
            <CardTitle className="text-golden">1. Information We Collect</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold text-white">1.1 Personal Information</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• <strong>Name:</strong> For personalized service and communication</li>
              <li>• <strong>Email Address:</strong> For account management and service delivery</li>
              <li>• <strong>Phone Number:</strong> For WhatsApp updates and support</li>
              <li>• <strong>Telegram Username:</strong> For group access and alert delivery</li>
              <li>• <strong>Payment Information:</strong> Processed securely through Razorpay</li>
            </ul>

            <h4 className="font-semibold text-white">1.2 Usage Information</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Website interaction data and preferences</li>
              <li>• Service usage patterns and engagement metrics</li>
              <li>• Device information and browser details</li>
              <li>• IP address and location data (for security purposes)</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-golden/20 mb-8">
          <CardHeader>
            <CardTitle className="text-golden">2. How We Use Your Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold text-white">2.1 Service Delivery</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Provide trading insights and educational content</li>
              <li>• Send automated alerts and market updates</li>
              <li>• Grant access to Telegram groups and channels</li>
              <li>• Process payments and manage subscriptions</li>
            </ul>

            <h4 className="font-semibold text-white">2.2 Communication</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Send service confirmations and updates</li>
              <li>• Provide customer support and assistance</li>
              <li>• Share important announcements and changes</li>
              <li>• Respond to inquiries and feedback</li>
            </ul>

            <h4 className="font-semibold text-white">2.3 Improvement and Analytics</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Analyze service performance and user satisfaction</li>
              <li>• Improve our trading strategies and content quality</li>
              <li>• Develop new features and services</li>
              <li>• Ensure platform security and prevent fraud</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-golden/20 mb-8">
          <CardHeader>
            <CardTitle className="text-golden">3. Information Sharing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold text-white">3.1 Third-Party Services</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• <strong>Razorpay:</strong> For secure payment processing</li>
              <li>• <strong>Email Services:</strong> For automated email delivery</li>
              <li>• <strong>Telegram API:</strong> For group management and alerts</li>
              <li>• <strong>Analytics Tools:</strong> For website performance monitoring</li>
            </ul>

            <h4 className="font-semibold text-white">3.2 We DO NOT Share</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Personal information with advertisers or marketers</li>
              <li>• Trading preferences or financial information</li>
              <li>• Contact details with third parties for promotional purposes</li>
              <li>• Any data for purposes unrelated to our services</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-golden/20 mb-8">
          <CardHeader>
            <CardTitle className="text-golden">4. Data Security</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold text-white">4.1 Security Measures</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• SSL encryption for all data transmission</li>
              <li>• Secure database storage with access controls</li>
              <li>• Regular security audits and updates</li>
              <li>• PCI DSS compliant payment processing</li>
            </ul>

            <h4 className="font-semibold text-white">4.2 Data Retention</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Account information retained while services are active</li>
              <li>• Payment records kept for tax and legal compliance (7 years)</li>
              <li>• Marketing preferences honored until changed by user</li>
              <li>• Inactive accounts may be archived after 2 years</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-golden/20 mb-8">
          <CardHeader>
            <CardTitle className="text-golden">5. Your Rights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold text-white">5.1 Data Access and Control</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Request access to your personal data</li>
              <li>• Update or correct your information</li>
              <li>• Delete your account and associated data</li>
              <li>• Opt-out of non-essential communications</li>
            </ul>

            <h4 className="font-semibold text-white">5.2 Communication Preferences</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Choose delivery methods for alerts (Email/Telegram)</li>
              <li>• Set frequency preferences for updates</li>
              <li>• Unsubscribe from marketing communications</li>
              <li>• Manage notification settings</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-golden/20 mb-8">
          <CardHeader>
            <CardTitle className="text-golden">6. Cookies and Tracking</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="font-semibold text-white">6.1 Cookies We Use</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• <strong>Essential Cookies:</strong> For basic website functionality</li>
              <li>• <strong>Authentication Cookies:</strong> To keep you logged in</li>
              <li>• <strong>Analytics Cookies:</strong> To understand site usage</li>
              <li>• <strong>Preference Cookies:</strong> To remember your settings</li>
            </ul>

            <h4 className="font-semibold text-white">6.2 Managing Cookies</h4>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Browser settings allow cookie management</li>
              <li>• Essential cookies required for service functionality</li>
              <li>• Analytics cookies can be disabled without affecting service</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-gray-900/50 border-golden/20 mb-8">
          <CardHeader>
            <CardTitle className="text-golden">7. Updates to Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="text-gray-300 space-y-2 ml-4">
              <li>• Policy updates will be posted on this page</li>
              <li>• Significant changes communicated via email</li>
              <li>• Continued use implies acceptance of updated terms</li>
              <li>• Version history available upon request</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="bg-golden/5 border-golden/30">
          <CardHeader>
            <CardTitle className="text-golden">Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-white mb-2">Privacy Concerns</h4>
                <p className="text-gray-300">chiragbrathod1988@gmail.com</p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">General Support</h4>
                <p className="text-gray-300">chiragbrathod1988@gmail.com</p>
              </div>
            </div>
            <div className="mt-4">
              <h4 className="font-semibold text-white mb-2">Postal Address</h4>
              <p className="text-gray-300">
                Upper Circuit Stocks<br />
                By Chirag Rathod<br />
                India
              </p>
            </div>
            <div className="mt-4 p-4 bg-black/30 rounded-lg">
              <p className="text-sm text-gray-400">
                <strong>Note:</strong> This privacy policy complies with applicable Indian data protection laws and regulations. 
                We are committed to protecting your privacy and maintaining the confidentiality of your personal information.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;